import type { AppProps } from 'next/app';
import React, {
    ReactElement,
    ReactNode,
    createContext,
    useContext,
    useEffect,
    useRef,
} from 'react';
import { NextPage } from 'next';
import 'swiper/css';
import 'swiper/css/grid';
import 'swiper/css/pagination';
import '@styles/global.scss';
import ErrorBoundary from '@components/error/ErrorBoundary';
import { enableStaticRendering, observer } from 'mobx-react-lite';
import { isServer } from '@src/utils/util';
import { appWithTranslation } from 'next-i18next';
import nextI18NextConfig from 'next-i18next.config.js';
import { SWRConfig } from 'swr';
import { fetcher } from '@src/utils/fetcher';
import { GlobalStoreProvider } from '@src/hooks/use-global-store';
import { AppRoot } from '@components/AppRoot';
import '../utils/polyfills';
import { AdobeDataLayerProvider } from '@src/adobe-launch/data-layer-provider';
import { TrackerProvider } from '@src/hooks/tracker/use-page-trackers';
import 'antd-mobile/bundle/css-vars-patch.css';
import 'video.js/dist/video-js.css';
import loadingStore from '@mobx/stores/loading-store';
import LoadingST from '@components/loading-ST/LoadingST';
import { initVConsole } from '@src/utils/vconsole';
import { checkNetworkSpeed } from '@src/utils/network';
import { useRouter } from 'next/router';

export type NextPageWithLayout = NextPage & {
    getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
    Component: NextPageWithLayout;
};

enableStaticRendering(isServer);

const StoreContext = createContext({ loadingStore });

export const useStores = () => useContext(StoreContext);

function MyApp({ Component, pageProps }: AppPropsWithLayout) {
    const router = useRouter();
    const isRouteChanging = useRef(false);

    useEffect(() => {
        initVConsole();
        checkNetworkSpeed();
    }, []);

    useEffect(() => {
        // 首次进入页面（包括 F5、直接输入 URL、后退到本站点等）
        const navEntry = performance.getEntriesByType(
            'navigation'
        )[0] as PerformanceNavigationTiming;
        if (navEntry?.type === 'reload' || navEntry?.type === 'navigate') {
            loadingStore.show();
            const timer = setTimeout(() => {
                loadingStore.hide();
            }, 300);

            return () => clearTimeout(timer);
        }
    }, []);

    useEffect(() => {
        const handleRouteStart = () => {
            if (!isRouteChanging.current) {
                isRouteChanging.current = true;
                loadingStore.show();
            }
        };

        const handleRouteComplete = () => {
            if (isRouteChanging.current) {
                isRouteChanging.current = false;
                loadingStore.hide();
            }
        };

        const handleRouteError = () => {
            // 出错也要关掉 loading
            handleRouteComplete();
        };

        router.events.on('routeChangeStart', handleRouteStart);
        router.events.on('routeChangeComplete', handleRouteComplete);
        router.events.on('routeChangeError', handleRouteError);

        return () => {
            router.events.off('routeChangeStart', handleRouteStart);
            router.events.off('routeChangeComplete', handleRouteComplete);
            router.events.off('routeChangeError', handleRouteError);
        };
    }, [router.events]);

    return (
        <StoreContext.Provider value={{ loadingStore }}>
            <div className={`text-base ${pageProps?._nextI18Next?.initialLocale || 'en-US'}`}>
                <ErrorBoundary>
                    <GlobalStoreProvider>
                        <AdobeDataLayerProvider>
                            <TrackerProvider>
                                <SWRConfig
                                    value={{
                                        fetcher: fetcher,
                                        revalidateIfStale: false,
                                        revalidateOnFocus: false,
                                    }}
                                >
                                    <LoadingST />
                                    <AppRoot page={Component} pageProps={pageProps} />
                                </SWRConfig>
                            </TrackerProvider>
                        </AdobeDataLayerProvider>
                    </GlobalStoreProvider>
                </ErrorBoundary>
            </div>
        </StoreContext.Provider>
    );
}

export default appWithTranslation(observer(MyApp), nextI18NextConfig);
